import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { useQuery, useQueries } from 'react-query';
import fetch from 'utils/fetch';

//// STATIC DICTIONARIES

export enum SearchDictionaryVariant {
  AID_TYPE = 'AID_TYPE',
  ARCHIVAL_UNIT_TYPE = 'ARCHIVAL_UNIT_TYPE',
  COLLECTION_ACCESSIBILITY = 'COLLECTION_ACCESSIBILITY',
  COLLECTION_GROUPS = 'COLLECTION_GROUPS',
  ORIGINATOR_IDENTIFIERS = 'ORIGINATOR_IDENTIFIERS',
  ARCHIVAL_UNIT_IDENTIFIERS = 'ARCHIVAL_UNIT_IDENTIFIERS',
  //
  PREDOMINANT_LANGUAGES = 'PREDOMINANT_LANGUAGES',
  EAD_UNIT_LANG_MATERIALS = 'EAD_UNIT_LANG_MATERIALS',
  ARCHIVAL_COLLECTION_LANGUAGES = 'ARCHIVAL_COLLECTION_LANGUAGES',
  AIDS_LANG_MATERIALS = 'AIDS_LANG_MATERIALS',
}

export const useDictionaryFetch = <T,>(
  variant: SearchDictionaryVariant,
  url: string,
  postProcess?: (data: unknown) => T
) => {
  const { data, isLoading } = useQuery(
    ['dict', SearchDictionaryVariant[variant]],
    () =>
      fetch(url).then(async (response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        } else {
          const json = await response.json();
          if (postProcess) {
            return postProcess(json) as T;
          } else return json as T;
          //return (await response.json()) as T;
        }
      })
  );
  return { data, isLoading };
};

export type DictionaryOption = { label: string; id: string };

type DictionarySource = {
  data: DictionaryOption[];
};

type DictionaryContextType = {
  sources: Record<string, DictionarySource>;
  loading: boolean;
};

export const SearchDictionaryContext = createContext<DictionaryContextType>({
  sources: {},
  loading: false,
});

/**
 * Convert string array to {id, label} array
 */
const postProcess = (data: unknown): DictionaryOption[] => {
  return (data as string[]).map((s) => ({ id: s, label: s }));
};

const dictDefinitions: Record<
  SearchDictionaryVariant,
  { url: string; postProcess?: (data: unknown) => DictionaryOption[] }
> = {
  AID_TYPE: { url: '/api/archival-aid-metadata/list-types', postProcess },

  ARCHIVAL_UNIT_IDENTIFIERS: {
    url: '/api/archival-aid-metadata/list-reference-types',
  },
  ARCHIVAL_UNIT_TYPE: { url: '/api/archival-aid-metadata/ead/list-unit-kinds' },
  COLLECTION_ACCESSIBILITY: {
    url: '/api/archival-collection/list-accessibilities',
    postProcess,
  },
  COLLECTION_GROUPS: {
    url: '/api/archival-collection/list-groups',
    postProcess,
  },
  ORIGINATOR_IDENTIFIERS: { url: '/api/originator/list-identifier-types' },
  PREDOMINANT_LANGUAGES: {
    url: '/api/search/predominant-languages',
    postProcess,
  },
  EAD_UNIT_LANG_MATERIALS: { url: '/api/search/lang-materials', postProcess },
  ARCHIVAL_COLLECTION_LANGUAGES: {
    url: '/api/search/archival-collection-languages',
    postProcess,
  },
  AIDS_LANG_MATERIALS: { url: '/api/search/lang-materials', postProcess },
};

const useDictionariesFetch = (dicts: typeof dictDefinitions) => {
  const [sources, setSources] = useState<Record<string, DictionarySource>>({});
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  const result = useQueries(
    Object.keys(dicts).map((k) => {
      const key = k as SearchDictionaryVariant;
      return {
        queryKey: ['dict', key, dicts[key].url],
        queryFn: async () => {
          try {
            const r = await fetch(dicts[key].url);
            const json = await r.json();
            if (dicts[key].postProcess) {
              return {
                key,
                data: dicts[key]?.postProcess?.(json) as DictionaryOption[],
              };
            } else return { key, data: json as DictionaryOption[] };
          } catch (error) {
            return { key, data: [] };
          }
        },
      };
    })
  );

  useEffect(() => {
    if (done) {
      return;
    }
    const loading = result.some((q) => q.isLoading);
    setLoading(loading);

    if (!loading && result.every((q) => q.isFetched)) {
      const sources = result.reduce((acc, current) => {
        acc[current.data?.key ?? ''] = { data: current?.data?.data ?? [] };
        return acc;
      }, {} as Record<string, DictionarySource>);
      setSources(sources);
      setDone(true);
    }
  }, [result, done]);

  return { loading, sources };
};

export const SearchDictionaryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { loading, sources } = useDictionariesFetch(dictDefinitions);

  return (
    <SearchDictionaryContext.Provider value={{ sources, loading }}>
      {children}
    </SearchDictionaryContext.Provider>
  );
};
